import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ProviderDataService from '../../services/provider.service';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';

const ProviderList = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { hasPermission } = useAuth();

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const response = await ProviderDataService.getAll({ search: searchTerm });
      setProviders(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching providers:', error);
      showSnackbar(t('providers.errorLoadingProviders') + ': ' + (error.response?.data?.message || error.message), 'error');
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm !== undefined) {
        fetchProviders();
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleDelete = (id) => {
    setDeleteDialog({ open: true, id });
  };

  const confirmDelete = async () => {
    try {
      await ProviderDataService.delete(deleteDialog.id);
      showSnackbar(t('providers.providerDeletedSuccess'));
      fetchProviders();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error deleting provider';
      showSnackbar(errorMessage, 'error');
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const toggleActive = async (provider) => {
    try {
      await ProviderDataService.update(provider.id, {
        ...provider,
        is_active: !provider.is_active
      });
      showSnackbar(t('providers.providerUpdatedSuccess'));
      fetchProviders();
    } catch (error) {
      showSnackbar(t('providers.errorUpdatingProvider'), 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };


  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">{t('providers.title')}</Typography>
            {hasPermission('providers.new') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/providers/new')}
              >
                {t('providers.newProvider')}
              </Button>
            )}
          </Box>

          <TextField
            placeholder={t('providers.searchProviders')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('providers.name')}</TableCell>
                  <TableCell>{t('providers.email')}</TableCell>
                  <TableCell>{t('providers.phone')}</TableCell>
                  <TableCell>{t('providers.address')}</TableCell>
                  <TableCell>{t('providers.status')}</TableCell>
                  <TableCell align="center">{t('providers.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : providers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {t('providers.noProvidersFound')}
                    </TableCell>
                  </TableRow>
                ) : (
                  providers.map((provider) => (
                    <TableRow key={provider.id} hover>
                      <TableCell>{provider.name || '-'}</TableCell>
                      <TableCell>{provider.email || '-'}</TableCell>
                      <TableCell>{provider.phone || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {provider.address || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={provider.is_active}
                              onChange={() => toggleActive(provider)}
                              size="small"
                            />
                          }
                          label={provider.is_active ? 'Active' : 'Inactive'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/providers/${provider.id}/edit`)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(provider.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>{t('providers.deleteProvider')}</DialogTitle>
        <DialogContent>
          <Typography>{t('providers.confirmDeletion')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProviderList;

